import { useCallback, useEffect, useMemo } from "react"
import { Tabs, TabList, TabPanels, TabPanel } from "@chakra-ui/react"
import { localUserIdAtom, usersSelector } from "../../recoil/atoms/users"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { hasPermission } from "../../util"
import Checkout from "./checkout"
import { chatIsReadSelector } from "../../recoil/atoms/chats"
import { useRecoilState } from "recoil"
import layoutStates from "../../recoil/atoms/layout"
import { SidePanelView } from "./views/SidePanelView"
import { ModeratorView } from "./views/ModeratorView"
import { brandingSelector } from "../../recoil/atoms/room"
import { getFocus } from "../../util/getFocus"
import { shoppableVideosSelector } from "../../recoil/selectors/shoppable-videos-selector"
import useConfig from "src/hooks/useConfig"

const UserPermission = window.MeetOnline?.UserPermission
const UserType = window.MeetOnline?.UserType

const audienceNoTab = (
  <Tabs variant="line" orientation="vertical">
    <TabPanels>
      <TabPanel display="none">
        <Checkout />
      </TabPanel>
    </TabPanels>
    <TabList
      display="none"
      textColor="gray.500"
      bg="#fff"
      minW="75px"
    ></TabList>
  </Tabs>
)

export const ServicesPanel = () => {
  const { defaultFullScreen } = useConfig()
  const [sidebarOpen, setSidebarOpen] = useRecoilState<boolean>(
    layoutStates.openSidebar
  )
  useEffect(() => {
    setSidebarOpen(!defaultFullScreen)
    setTargetTabIndex(0)
  }, [defaultFullScreen])
  // const setChatIsRead = useSetRecoilState(chatIsReadAtom)
  const setChatIsRead = useSetRecoilState(chatIsReadSelector)
  const [targetTabIndex, setTargetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabled
  )

  const hasShoppableVideos = useRecoilValue(shoppableVideosSelector)

  const handleTabsChange = useCallback(
    index => {
      setTargetTabIndex(index)
    },
    [setTargetTabIndex]
  )

  const [gridViewEnabled, setGridViewEnabled] = useRecoilState<boolean>(
    layoutStates.gridViewEnabled
  )
  const localUserId = useRecoilValue(localUserIdAtom)
  const _localUser = useRecoilValue(usersSelector(localUserId))

  const roomStyles = useRecoilValue(brandingSelector)
  const servicePanelBackgroundColor = useMemo(
    () => roomStyles?.servicePanelColor ?? "#F7FAFC",
    [roomStyles?.servicePanelColor]
  )
  // const setIsChatIsScroll = useSetRecoilState(chatScrollAtom)

  const handleClickChatTab = useCallback(() => {
    getFocus("expanding-input")
    // setIsChatIsScroll(true)
    setChatIsRead(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickOneTabChat = () => {
    // setIsChatIsScroll(true)
    setChatIsRead(true)
  }

  const tabButtonStyle = useMemo(() => {
    return {
      background: roomStyles?.accessButtonBackgroundColor ?? "gray.100",
      color: roomStyles?.accessButtonTextColor ?? "#718096",
      stroke: roomStyles?.accessButtonTextColor ?? "#718096",
      _hover: {
        backgroundColor: roomStyles?.accessButtonMouseoverColor ?? "#edf2f7",
        color: roomStyles?.accessButtonTextMouseoverColor ?? "#000",
        stroke: roomStyles?.accessButtonTextMouseoverColor ?? "#000"
      },
      _selected: {
        backgroundColor: roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
        textColor: roomStyles?.accessButtonSelectedTextColor ?? "#000",
        stroke: roomStyles?.accessButtonSelectedTextColor ?? "#000"
      },
      _focus: {},
      margin: 0
    }
  }, [
    roomStyles?.accessButtonBackgroundColor,
    roomStyles?.accessButtonMouseoverColor,
    roomStyles?.accessButtonSelectedColor,
    roomStyles?.accessButtonSelectedTextColor,
    roomStyles?.accessButtonTextColor,
    roomStyles?.accessButtonTextMouseoverColor
  ])

  const liveSellButtonProps = useMemo(() => {
    return {
      audienceButtonLiveSellIcon: roomStyles?.AudienceButtonLiveSellIcon,
      audienceButtonLiveSellText: roomStyles?.AudienceButtonLiveSellText
    }
  }, [
    roomStyles?.AudienceButtonLiveSellIcon,
    roomStyles?.AudienceButtonLiveSellText
  ])

  if (_localUser?.type === UserType.AUDIENCE) {
    if (
      hasPermission(_localUser!.permissions, UserPermission.canSeeUserList) ||
      hasPermission(_localUser!.permissions, UserPermission.canUseChat) ||
      hasPermission(_localUser!.permissions, UserPermission.canSeeHistory) ||
      hasShoppableVideos
    ) {
      return (
        <SidePanelView
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          gridViewEnabled={gridViewEnabled}
          setGridViewEnabled={setGridViewEnabled}
          servicePanelBackgroundColor={servicePanelBackgroundColor}
          tabButtonStyle={tabButtonStyle}
          handleClickChatTab={handleClickChatTab}
          liveSellButtonProps={liveSellButtonProps}
          canSeeUserListTab={hasPermission(
            _localUser!.permissions,
            UserPermission.canSeeUserList
          )}
          canSeeChatTab={hasPermission(
            _localUser!.permissions,
            UserPermission.canUseChat
          )}
          canSeeHistoryTab={hasPermission(
            _localUser!.permissions,
            UserPermission.canSeeHistory
          )}
          targetTabIndex={targetTabIndex}
          handleTabsChange={handleTabsChange}
          handleClickOneTabChat={handleClickOneTabChat}
        />
      )
    }
    return audienceNoTab
  }

  if (_localUser?.type === UserType.BROADCASTER) {
    // const hasBannerPermission = hasPermission(
    //   _localUser!.permissions,
    //   UserPermission.canSeeBannersList
    // )
    const hasHistoryPermission = hasPermission(
      _localUser!.permissions,
      UserPermission.canSeeHistory
    )
    const hasChatPermission = hasPermission(
      _localUser!.permissions,
      UserPermission.canUseChat
    )
    const hasUserListPermission = hasPermission(
      _localUser!.permissions,
      UserPermission.canSeeUserList
    )

    if (
      hasUserListPermission ||
      hasChatPermission ||
      hasHistoryPermission ||
      hasShoppableVideos
    ) {
      return (
        <SidePanelView
          canSeeChatTab={hasChatPermission}
          liveSellButtonProps={liveSellButtonProps}
          canSeeUserListTab={hasUserListPermission}
          sidebarOpen={sidebarOpen}
          gridViewEnabled={gridViewEnabled}
          setGridViewEnabled={setGridViewEnabled}
          setSidebarOpen={setSidebarOpen}
          // hasBannerPermission={hasBannerPermission}
          canSeeHistoryTab={hasHistoryPermission}
          servicePanelBackgroundColor={servicePanelBackgroundColor}
          tabButtonStyle={tabButtonStyle}
          targetTabIndex={targetTabIndex}
          handleTabsChange={handleTabsChange}
          handleClickChatTab={handleClickChatTab}
          handleClickOneTabChat={handleClickOneTabChat}
        />
      )
    }
    return audienceNoTab
  }

  if (_localUser?.type === UserType.MODERATOR) {
    return (
      <ModeratorView
        sidebarOpen={sidebarOpen}
        liveSellButtonProps={liveSellButtonProps}
        setSidebarOpen={setSidebarOpen}
        servicePanelBackgroundColor={servicePanelBackgroundColor}
        tabButtonStyle={tabButtonStyle}
        targetTabIndex={targetTabIndex}
        handleTabsChange={handleTabsChange}
        handleClickChatTab={handleClickChatTab}
      />
    )
  }

  return (
    <SidePanelView
      liveSellButtonProps={liveSellButtonProps}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      gridViewEnabled={gridViewEnabled}
      setGridViewEnabled={setGridViewEnabled}
      servicePanelBackgroundColor={servicePanelBackgroundColor}
      tabButtonStyle={tabButtonStyle}
      canSeeUserListTab={hasPermission(
        _localUser?.permissions,
        UserPermission.canSeeUserList
      )}
      canSeeChatTab={hasPermission(
        _localUser?.permissions,
        UserPermission.canUseChat
      )}
      canSeeHistoryTab={hasPermission(
        _localUser?.permissions,
        UserPermission.canSeeHistory
      )}
      targetTabIndex={targetTabIndex}
      handleTabsChange={handleTabsChange}
      handleClickChatTab={handleClickChatTab}
      handleClickOneTabChat={handleClickOneTabChat}
    />
  )
}
